import { Ticket, User, Site } from '../types';

/**
 * Service for exporting tickets data to various formats
 */
export class ExportService {
    /**
     * Export tickets to CSV format
     */
    static exportToCSV(tickets: Ticket[], filename: string = 'tickets.csv'): void {
        const headers = [
            'Número',
            'Tienda',
            'Tipo',
            'Categoría',
            'Subcategoría',
            'Prioridad',
            'Estado',
            'Descripción',
            'Asignado a',
            'Creado',
            'Actualizado',
            'SLA',
            'Impacto',
            'Urgencia'
        ];

        const csvRows = [
            headers.join(','),
            ...tickets.map(ticket => [
                ticket.ticketNumber || ticket.id,
                `"${ticket.siteName}"`,
                ticket.type === 'INCIDENT' ? 'Incidente' : 'Solicitud',
                `"${ticket.category}"`,
                `"${ticket.subcategory}"`,
                ticket.priority,
                this.getStatusLabel(ticket.status),
                `"${ticket.description.replace(/"/g, '""')}"`,
                `"${ticket.assignedToName || 'Sin asignar'}"`,
                new Date(ticket.createdAt).toLocaleString('es-CL'),
                new Date(ticket.updatedAt).toLocaleString('es-CL'),
                new Date(ticket.slaDue).toLocaleString('es-CL'),
                ticket.impact || '',
                ticket.urgency || ''
            ].join(','))
        ];

        const csvContent = csvRows.join('\n');
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        this.downloadFile(blob, filename);
    }

    /**
     * Export tickets to Excel format (using CSV with .xlsx extension for simplicity)
     */
    static exportToExcel(tickets: Ticket[], filename: string = 'tickets.xlsx'): void {
        // For a simple implementation, we'll use CSV with .xlsx extension
        // For a full Excel implementation, consider using libraries like 'xlsx' or 'exceljs'
        this.exportToCSV(tickets, filename.replace('.xlsx', '.csv'));
    }

    /**
     * Export tickets to JSON format
     */
    static exportToJSON(tickets: Ticket[], filename: string = 'tickets.json'): void {
        const jsonContent = JSON.stringify(tickets, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        this.downloadFile(blob, filename);
    }

    /**
     * Generate PDF report (basic HTML-to-PDF approach)
     */
    static generatePDFReport(tickets: Ticket[], title: string = 'Reporte de Tickets'): void {
        // Open print dialog with formatted content
        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            alert('Por favor permita ventanas emergentes para generar el PDF');
            return;
        }

        const htmlContent = this.generateReportHTML(tickets, title);
        printWindow.document.write(htmlContent);
        printWindow.document.close();

        // Trigger print after content loads
        printWindow.onload = () => {
            printWindow.print();
        };
    }

    /**
     * Generate HTML for PDF report
     */
    private static generateReportHTML(tickets: Ticket[], title: string): string {
        const date = new Date().toLocaleDateString('es-CL');
        const time = new Date().toLocaleTimeString('es-CL');

        // Statistics
        const byStatus = this.groupBy(tickets, 'status');
        const byPriority = this.groupBy(tickets, 'priority');
        const bySite = this.groupBy(tickets, 'siteName');
        const total = tickets.length;

        // Helper for stats rows
        const renderStatRow = (label: string, count: number) => `
            <tr>
                <td style="padding: 4px; border: none;">${label}</td>
                <td style="padding: 4px; border: none; text-align: right; font-weight: bold;">${count}</td>
                <td style="padding: 4px; border: none; text-align: right; color: #666;">${Math.round(count / total * 100)}%</td>
            </tr>
        `;

        return `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${title}</title>
                <style>
                    @media print { @page { margin: 1.5cm; } }
                    body { font-family: 'Segoe UI', Arial, sans-serif; padding: 20px; color: #333; line-height: 1.4; }
                    h1 { color: #000; border-bottom: 3px solid #000; padding-bottom: 10px; margin-bottom: 5px; }
                    .header-meta { color: #666; font-size: 12px; margin-bottom: 30px; display: flex; justify-content: space-between; }
                    
                    .summary-section { display: flex; gap: 20px; margin-bottom: 30px; }
                    .summary-card { flex: 1; background: #f9f9f9; padding: 15px; border-radius: 8px; border: 1px solid #eee; }
                    .summary-title { font-weight: bold; border-bottom: 1px solid #ddd; margin-bottom: 10px; padding-bottom: 5px; font-size: 12px; text-transform: uppercase; color: #555; }
                    .stat-table { width: 100%; font-size: 11px; }

                    .main-table { width: 100%; border-collapse: collapse; font-size: 10px; }
                    .main-table th { background-color: #000; color: white; padding: 8px; text-align: left; font-size: 11px; }
                    .main-table td { padding: 8px; border-bottom: 1px solid #ddd; }
                    .main-table tr:hover { background-color: #f5f5f5; }
                    
                    .priority-P1 { color: #dc2626; font-weight: bold; }
                    .priority-P2 { color: #ea580c; font-weight: bold; }
                    .priority-P3 { color: #0284c7; }
                    .priority-P4 { color: #65a30d; }
                    
                    .footer { margin-top: 30px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 10px; }
                </style>
            </head>
            <body>
                <h1>${title}</h1>
                <div class="header-meta">
                    <span>Generado el ${date} a las ${time}</span>
                    <span><strong>Total: ${total} tickets</strong></span>
                </div>

                <div class="summary-section">
                    <div class="summary-card">
                        <div class="summary-title">Por Estado</div>
                        <table class="stat-table">
                            ${Object.entries(byStatus)
                .sort((a, b) => b[1].length - a[1].length)
                .map(([status, items]) => renderStatRow(this.getStatusLabel(status), items.length))
                .join('')}
                        </table>
                    </div>
                    <div class="summary-card">
                        <div class="summary-title">Por Prioridad</div>
                        <table class="stat-table">
                            ${Object.entries(byPriority)
                .sort()
                .map(([priority, items]) => renderStatRow(priority, items.length))
                .join('')}
                        </table>
                    </div>
                    <div class="summary-card">
                        <div class="summary-title">Top Tiendas</div>
                        <table class="stat-table">
                            ${Object.entries(bySite)
                .sort((a, b) => b[1].length - a[1].length)
                .slice(0, 5)
                .map(([site, items]) => renderStatRow(site, items.length))
                .join('')}
                        </table>
                    </div>
                </div>

                <table class="main-table">
                    <thead>
                        <tr>
                            <th>Número</th>
                            <th>Tienda</th>
                            <th>Categoría</th>
                            <th>Prioridad</th>
                            <th>Estado</th>
                            <th>Creado</th>
                            <th>Asignado</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${tickets.map(ticket => `
                            <tr>
                                <td>${ticket.ticketNumber || ticket.id.substring(0, 8)}</td>
                                <td>${ticket.siteName}</td>
                                <td>${ticket.category} - ${ticket.subcategory}</td>
                                <td class="priority-${ticket.priority}">${ticket.priority}</td>
                                <td>${this.getStatusLabel(ticket.status)}</td>
                                <td>${new Date(ticket.createdAt).toLocaleDateString('es-CL')}</td>
                                <td>${ticket.assignedToName || 'Sin asignar'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>
                <div class="footer">
                    Adidas Chile - Sistema de Tickets - Documento Confidencial
                </div>
            </body>
            </html>
        `;
    }

    /**
     * Create summary report with statistics
     */
    static generateSummaryReport(tickets: Ticket[]): string {
        const totalTickets = tickets.length;
        const byStatus = this.groupBy(tickets, 'status');
        const byPriority = this.groupBy(tickets, 'priority');
        const bySite = this.groupBy(tickets, 'siteName');

        return `
=== REPORTE DE TICKETS - ${new Date().toLocaleDateString('es-CL')} ===

RESUMEN GENERAL:
- Total de tickets: ${totalTickets}

POR ESTADO:
${Object.entries(byStatus).map(([status, items]) =>
            `- ${this.getStatusLabel(status)}: ${items.length} (${Math.round(items.length / totalTickets * 100)}%)`
        ).join('\n')}

POR PRIORIDAD:
${Object.entries(byPriority).map(([priority, items]) =>
            `- ${priority}: ${items.length} (${Math.round(items.length / totalTickets * 100)}%)`
        ).join('\n')}

TOP 5 TIENDAS CON MÁS TICKETS:
${Object.entries(bySite)
                .sort((a, b) => b[1].length - a[1].length)
                .slice(0, 5)
                .map(([site, items], idx) => `${idx + 1}. ${site}: ${items.length} tickets`)
                .join('\n')}
        `.trim();
    }

    /**
     * Helper: Group array by key
     */
    private static groupBy<T>(array: T[], key: keyof T): Record<string, T[]> {
        return array.reduce((result, item) => {
            const group = String(item[key]);
            if (!result[group]) result[group] = [];
            result[group].push(item);
            return result;
        }, {} as Record<string, T[]>);
    }

    /**
     * Helper: Download file
     */
    private static downloadFile(blob: Blob, filename: string): void {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        window.URL.revokeObjectURL(url);
    }

    /**
     * Helper: Get status label
     */
    private static getStatusLabel(status: string): string {
        const labels: Record<string, string> = {
            'OPEN': 'Abierto',
            'IN_PROGRESS': 'En Progreso',
            'IN_TRANSIT': 'En Tránsito',
            'ON_SITE': 'En Sitio',
            'WAITING_PARTS': 'Esperando Repuestos',
            'WAITING_INFO': 'Esperando Información',
            'RESOLVED': 'Resuelto',
            'CLOSED': 'Cerrado'
        };
        return labels[status] || status;
    }

    /**
     * Helper: Get priority CSS class
     */
    private static getPriorityClass(priority: string): string {
        if (priority === 'P1' || priority === 'P2') return 'high';
        if (priority === 'P3') return 'medium';
        return 'low';
    }
}
